import {
  Component,
  inject,
  DOCUMENT,
  computed,
  ChangeDetectionStrategy,
} from '@angular/core';
import {RouterOutlet} from '@angular/router';
import {ThemeService} from '@/app/core/services/theme.service';
import {SettingsService} from '@/app/features/settings/services/settings.service';
import {layoutStore} from '@/app/layout/store/layout.store';
import {TranslateService, TranslatePipe} from '@ngx-translate/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {PrimeNG} from 'primeng/config';
import type {Translation} from 'primeng/api';
import {Toast} from 'primeng/toast';
import {ConfirmDialog} from 'primeng/confirmdialog';
import {LoaderComponent} from '@/app/core/components/loader/loader';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, Toast, ConfirmDialog, LoaderComponent, TranslatePipe],
  templateUrl: './app.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true,
})
export class App {
  protected title = 'Muwarid';
  protected readonly toastPosition = computed<'top-right' | 'top-left'>(() =>
    layoutStore.isRtl() ? 'top-left' : 'top-right',
  );

  constructor() {
    const theme = inject(ThemeService);
    const mode = theme.init();
    layoutStore.setTheme(mode);

    const settings = inject(SettingsService);
    settings.init();

    const translate = inject(TranslateService);
    const lang = layoutStore.language();
    translate.use(lang);
    // PrimeNG widgets (date picker month/day names) follow the app language.
    const primeng = inject(PrimeNG);
    translate
      .stream('primeng')
      .pipe(takeUntilDestroyed())
      .subscribe((t: Translation) => primeng.setTranslation(t));

    const doc = inject(DOCUMENT);
    const html = doc.querySelector('html');
    if (html) {
      html.setAttribute('dir', lang === 'ar' ? 'rtl' : 'ltr');
      html.setAttribute('lang', lang);
    }
  }
}
